import { useWebchatMessages } from '@/hooks/use-webchat-messages/use-webchat-messages';
import { useWebchatActions } from '@/hooks/use-webchat-actions/use-webchat-actions';
import { useWebchatStore } from '@/store/webchat.store';
import {
  getVisitorLabel,
  isAiCallSession,
  WEBCHAT_STATUS_COLORS,
} from '@/services/webchat-service/webchat.service.types';
import { Button, Input, Tag } from 'antd';
import { ArrowDownOutlined, SendOutlined } from '@ant-design/icons';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { ParseKeys } from 'i18next';
import { ChatBubble } from '../chat-bubble/chat-bubble';
import { LiveTranscriptBubble } from '../live-transcript-bubble/live-transcript-bubble';
import { BridgeStatusBar } from '../bridge-status-bar/bridge-status-bar';
import { ScreenShareViewer } from '../screen-share-viewer/screen-share-viewer';
import { useScreenShareStore } from '@/store/screen-share.store';
import styles from './chat-panel.module.scss';

// Dictionary keys, resolved at render time; `idle` has no label.
const STATUS_LABELS: Record<string, Record<string, ParseKeys<'webchat'> | ''>> = {
  visitor: {
    transcribing: 'bridge.activity.visitor.transcribing',
    translating: 'bridge.activity.visitor.translating',
    speaking: 'bridge.activity.visitor.speaking',
    idle: '',
  },
  agent: {
    transcribing: 'bridge.activity.agent.transcribing',
    translating: 'bridge.activity.agent.translating',
    speaking: 'bridge.activity.agent.speaking',
    idle: '',
  },
};

const BridgeStatusIndicator = ({ conversationId, language }: { conversationId: number; language: string }) => {
  const { t } = useTranslation(['webchat', 'common']);
  const bridgeStatus = useWebchatStore((s) => s.bridgeStatus[conversationId]);
  const visitorStatus = bridgeStatus?.visitor || 'idle';
  const agentStatus = bridgeStatus?.agent || 'idle';

  const activeLabelKey =
    STATUS_LABELS.visitor[visitorStatus] ||
    STATUS_LABELS.agent[agentStatus] ||
    '';
  const activeLabel = activeLabelKey ? t(`webchat:${activeLabelKey}`) : '';

  return (
    <span>
      {t('webchat:chatPanel.bridgeActive', { language: language || t('common:states.detecting') })}
      {activeLabel && <span style={{ marginLeft: 8, opacity: 0.8, fontStyle: 'italic' }}>| {activeLabel}</span>}
    </span>
  );
};

interface ChatPanelProps {
  onTyping: (conversationId: number) => void;
}

const SCROLL_THRESHOLD = 100;

export const ChatPanel = ({ onTyping }: ChatPanelProps) => {
  const { t } = useTranslation('webchat');
  // Narrow Zustand subscriptions to only the slices this panel reads.
  // Subscribing to the whole store re-rendered the entire chat panel
  // on every transcript turn / typing indicator / bridge-status update
  // for *any* session — which read as the whole middle column
  // flickering whenever calls were active. We now derive the selected
  // session inside the selector so the consumer only re-renders when
  // the selected session itself changes.
  const selectedConversationId = useWebchatStore((s) => s.selectedConversationId);
  const session = useWebchatStore((s) =>
    s.selectedConversationId == null
      ? undefined
      : s.sessions.find((c) => c.id === s.selectedConversationId)
  );
  const conversationMessages = useWebchatStore((s) =>
    s.selectedConversationId == null ? [] : (s.messages[s.selectedConversationId] || [])
  );
  const isTyping = useWebchatStore((s) =>
    s.selectedConversationId == null ? false : Boolean(s.visitorTyping[s.selectedConversationId])
  );
  const isDisconnected = useWebchatStore((s) =>
    s.selectedConversationId == null ? false : s.disconnectedVisitors.has(s.selectedConversationId)
  );
  const bridgeAgentLanguage = useWebchatStore((s) => s.bridgeAgentLanguage);
  // True when the selected conversation currently has an active visitor
  // screen share. Drives the inline ScreenShareViewer mount/unmount.
  const hasScreenShare = useScreenShareStore((s) =>
    selectedConversationId == null ? false : !!s.shares[selectedConversationId]
  );
  const { sendMessage, isSending, claim, isClaiming } = useWebchatActions();
  const [inputValue, setInputValue] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messageListRef = useRef<HTMLDivElement>(null);
  const [isNearBottom, setIsNearBottom] = useState(true);
  const [hasNewMessages, setHasNewMessages] = useState(false);

  const isAiCall = isAiCallSession(session);
  // Legacy RAI voice — same live_transcript_turns stream, fed by
  // AnalyticsEvents::ProcessService instead of LiveTranscriptService.
  const isAnalyticsCall = session?.type === 'AnalyticsCall';
  const hasLiveTranscript = isAiCall || isAnalyticsCall;
  // SMS threads use the same message-list rendering as webchat — the backend
  // serializes Message rows into the WebchatMessage shape so a single
  // ChatBubble path works for both.
  const isSms = session?.type === 'TextConversation';
  const liveTranscriptTurns = session?.live_transcript_turns ?? [];

  // Webchat + SMS sessions both load message history from the API. Voice
  // calls (AI or legacy) have no separate messages table — their content
  // lives entirely in session.live_transcript_turns.
  useWebchatMessages(hasLiveTranscript ? null : selectedConversationId);

  useEffect(() => {
    setInputValue('');
    setIsNearBottom(true);
    setHasNewMessages(false);
  }, [selectedConversationId]);

  const handleScroll = useCallback(() => {
    const el = messageListRef.current;
    if (!el) return;
    const nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < SCROLL_THRESHOLD;
    setIsNearBottom(nearBottom);
    if (nearBottom) setHasNewMessages(false);
  }, []);

  // Auto-scroll on new content. For webchat we react to messages length;
  // for AI calls we react to the live transcript turns length so streamed
  // turns also pin the view to the bottom. We intentionally only depend on
  // contentLength (not isNearBottom) to avoid re-running this effect on
  // every scroll — we read the latest isNearBottom from closure.
  const contentLength = hasLiveTranscript ? liveTranscriptTurns.length : conversationMessages.length;
  useEffect(() => {
    if (isNearBottom) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    } else {
      setHasNewMessages(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contentLength]);

  // Always scroll to bottom on conversation switch
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView();
  }, [selectedConversationId]);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    setHasNewMessages(false);
  }, []);

  const handleSend = useCallback(async () => {
    if (!inputValue.trim() || !selectedConversationId) return;
    const text = inputValue.trim();
    setInputValue('');
    await sendMessage({ id: selectedConversationId, text });
  }, [inputValue, selectedConversationId, sendMessage]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        handleSend();
      }
    },
    [handleSend]
  );

  const lastTypingSent = useRef(0);
  const throttledOnTyping = useMemo(
    () => (conversationId: number) => {
      const now = Date.now();
      if (now - lastTypingSent.current > 2000) {
        lastTypingSent.current = now;
        onTyping(conversationId);
      }
    },
    [onTyping]
  );

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      setInputValue(e.target.value);
      if (selectedConversationId) {
        throttledOnTyping(selectedConversationId);
      }
    },
    [selectedConversationId, throttledOnTyping]
  );

  if (!selectedConversationId || !session) {
    return (
      <div className={styles.chatPanel}>
        <div className={styles.emptyState}>
          <div className={styles.emptyIcon} aria-hidden="true">
            💬
          </div>
          <div className={styles.emptyTitle}>{t('chatPanel.empty.title')}</div>
          <div className={styles.emptyHint}>{t('chatPanel.empty.hint')}</div>
        </div>
      </div>
    );
  }

  const isLive = session.webchat_status === 'live';
  // SMS threads can be replied to whenever they're "active in the Command
  // Center" — i.e. the polymorphic command_center_active? predicate on the
  // backend. From the dashboard perspective: the session is in `sessions`
  // (the Live array). If end_reason is 'user' or a delivery failure the
  // backend would have already removed it from sessions, so any SMS we see
  // here is replyable.
  const isSmsReplyable = isSms;
  const visitorLabel = getVisitorLabel(session);

  return (
    <div className={styles.chatPanel}>
      <div className={styles.visitorHeader}>
        <div className={styles.headerInfo}>
          <span>{visitorLabel}</span>
          {isAiCall && <Tag color="purple">{t('chatPanel.aiCallTag')}</Tag>}
          {session.language && <Tag>{session.language}</Tag>}
          {isDisconnected && <Tag color="red">{t('visitor.disconnected')}</Tag>}
        </div>
        <div className={styles.headerActions}>
          <Tag color={WEBCHAT_STATUS_COLORS[session.webchat_status]}>
            {t(`status.${session.webchat_status}`)}
          </Tag>
        </div>
      </div>

      {isAiCall && (
        <BridgeStatusBar
          conversationId={session.id}
          callerLanguage={session.language}
          agentLanguage={bridgeAgentLanguage[session.id]}
          assignedAgentId={session.assigned_agent_id}
        />
      )}

      {hasScreenShare && <ScreenShareViewer conversationId={session.id} />}

      <div className={styles.messageListWrapper}>
        <div className={styles.messageList} ref={messageListRef} onScroll={handleScroll}>
          {hasLiveTranscript ? (
            <>
              {liveTranscriptTurns.length === 0 && (
                <div className={styles.typingIndicator}>{t('chatPanel.waitingForTranscript')}</div>
              )}
              {liveTranscriptTurns.map((turn, idx) => (
                <LiveTranscriptBubble key={`${turn.received_at}-${idx}`} turn={turn} />
              ))}
            </>
          ) : session.type === 'ChatConversation' || isSms ? (
            <>
              {conversationMessages.map((msg) => (
                <ChatBubble key={msg.id} message={msg} />
              ))}
              {isTyping && <div className={styles.typingIndicator}>{t('chatPanel.visitorTyping')}</div>}
            </>
          ) : (
            // AgentCall + Email — no live transcript stream wired yet.
            <div className={styles.typingIndicator}>
              {session.type === 'EmailConversation'
                ? t('chatPanel.emailThread')
                : t('chatPanel.voiceCall')}
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>
        {hasNewMessages && !isNearBottom && (
          <Button
            className={styles.newMessagesBtn}
            size="small"
            icon={<ArrowDownOutlined />}
            onClick={scrollToBottom}
          >
            {t('chatPanel.newMessages')}
          </Button>
        )}
      </div>

      {isAiCall ? (
        <div className={styles.inputArea}>
          <div className={styles.aiCallNotice}>
            {session.assigned_agent_id
              ? <BridgeStatusIndicator conversationId={session.id} language={session.language} />
              : t('chatPanel.listenOnlyAi')}
          </div>
        </div>
      ) : isAnalyticsCall ? (
        <div className={styles.inputArea}>
          <div className={styles.aiCallNotice}>
            {t('chatPanel.listenOnlyLive')}
          </div>
        </div>
      ) : session.type === 'ChatConversation' || isSmsReplyable ? (
        <div className={styles.inputArea}>
          {isSmsReplyable && !session.assigned_agent_id && (
            <Button
              size="small"
              onClick={() => claim({ id: session.id, translate: false })}
              loading={isClaiming}
              style={{ marginRight: 8 }}
            >
              {t('chatPanel.takeOver')}
            </Button>
          )}
          <Input.TextArea
            className={styles.textArea}
            value={inputValue}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            placeholder={
              isSmsReplyable
                ? session.assigned_agent_id
                  ? t('chatPanel.placeholder.smsAssigned')
                  : t('chatPanel.placeholder.smsUnassigned')
                : isLive
                  ? t('chatPanel.placeholder.live')
                  : t('chatPanel.placeholder.notLive')
            }
            disabled={!isSmsReplyable && !isLive}
            autoSize={{ minRows: 1, maxRows: 4 }}
          />
          <Button
            type="primary"
            icon={<SendOutlined />}
            onClick={handleSend}
            disabled={(!isSmsReplyable && !isLive) || !inputValue.trim()}
            loading={isSending}
          />
        </div>
      ) : (
        <div className={styles.inputArea}>
          <div className={styles.aiCallNotice}>
            {session.type === 'EmailConversation'
              ? t('chatPanel.readOnly.email')
              : t('chatPanel.readOnly.voice')}
          </div>
        </div>
      )}
    </div>
  );
};
